import { useDefaultInfo } from '@cart/ui'
import { useCartAuth, useCurrentContext, usePermissionCheck } from '@cartdotcom/auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useEffectOnceWhen } from 'rooks'
import { UseAccountConfirmFormProps } from '../AccountConfirmForm.types'
import { useAccountConfirmFormSubmit } from './useAccountConfirmFormSubmit'
import { validatePhone } from '@brand-console/utilities'

export const useAccountConfirmForm = ({ methods, log }: UseAccountConfirmFormProps) => {
  const [isFormLoading, setIsFormLoading] = useState(true)
  const [shouldShowForm, setShouldShowForm] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [, setRerenderKey] = useState(0)
  const { currentBusiness, currentOrganization, isLoaded } = useCurrentContext()
  const { phone, firstName, lastName } = useCartAuth()
  const canUpdateBiz = usePermissionCheck(
    'updateBusiness',
    currentBusiness?.id,
    currentOrganization?.id,
  )

  const {
    business: { name: defaultBizName },
  } = useDefaultInfo()

  const submitForm = useAccountConfirmFormSubmit({
    methods,
    log,
    setErrorMessage,
  })

  const { trigger, reset, getValues, watch } = methods

  useEffect(() => {
    const getShouldShowForm = async () => {
      if (
        !currentBusiness?.name ||
        currentBusiness?.name === defaultBizName ||
        !firstName ||
        !lastName ||
        !phone ||
        !(await validatePhone(phone))
      )
        setShouldShowForm(true)
      else setShouldShowForm(false)

      setIsFormLoading(false)
    }

    if (isLoaded) getShouldShowForm()
  }, [isLoaded])

  const resetForm = useCallback(() => {
    reset()
    setTimeout(() => phone && trigger('phone'), 10)
  }, [phone, reset, trigger])

  // Users may have an invalid phone number, so we need to show the error message
  useEffect(() => {
    if (!getValues('phone')) return
    setTimeout(() => {
      trigger('phone')
    }, 1)
  }, [getValues, trigger, shouldShowForm])

  useEffectOnceWhen(() => {
    // Need rerender once form is on the page to trigger validation
    setRerenderKey((prev) => prev + 1)
  }, shouldShowForm)

  return useMemo(
    () => ({
      submitForm,
      errorMessage,
      methods,
      formDefaultValues: {
        firstName,
        lastName,
        businessName: currentBusiness?.name === defaultBizName ? '' : currentBusiness?.name,
        phone: phone || '',
      },
      updateBizDisabled: !canUpdateBiz,
      shouldShowForm,
      isFormLoading,
      resetForm,
      setErrorMessage,
    }),
    [
      canUpdateBiz,
      currentBusiness?.name,
      currentOrganization?.name,
      defaultBizName,
      errorMessage,
      firstName,
      lastName,
      methods,
      phone,
      resetForm,
      shouldShowForm,
      submitForm,
    ],
  )
}
